import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import styles from "../styles/Hero.module.scss";
import { CONTENT } from "../constants/content";
import { readAlpsPillars } from "../constants/alps";
import CtaButton from "./ui/CtaButton";
import { setText } from "../constants/typography";
import { useIsDesktop } from "../hooks/useMediaQuery";
import heroImage from "../assets/images/caterina-hero1.jpg";
import hero480 from "../assets/images/caterina-hero1-480.webp";
import hero768 from "../assets/images/caterina-hero1-768.webp";
import hero1200 from "../assets/images/caterina-hero1-1200.webp";
import hero1600 from "../assets/images/caterina-hero1-1600.webp";

// WebP in srcSet, original JPEG as the universal fallback. A phone pulls the
// 480px variant (~16KB) instead of the 435KB original.
const HERO_SRCSET = [
  `${hero480} 480w`,
  `${hero768} 768w`,
  `${hero1200} 1200w`,
  `${hero1600} 1600w`,
].join(", ");

const EASE = [0.22, 1, 0.36, 1];

/**
 * The ALPS signature: the four pillars read straight out of the approved
 * method copy, set as one quiet caption line with each initial in the brand
 * red — so A · L · P · S is legible at a glance without ever becoming a
 * second information block.
 */
const AlpsCaption = ({ reduceMotion }) => {
  const pillars = readAlpsPillars();
  if (pillars.length !== 4) return null;

  return (
    <p className={styles.alpsCaption}>
      <span className="sr-only">{`ALPS: ${pillars.join(", ")}`}</span>
      {pillars.map((word, index) => (
        <motion.span
          key={word}
          className={styles.pillar}
          aria-hidden="true"
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.75 + index * 0.09, ease: EASE }}
        >
          <span className={styles.pillarInitial}>{word.charAt(0)}</span>
          {word.slice(1)}
        </motion.span>
      ))}
    </p>
  );
};

const Hero = () => {
  const { eyebrow, title, subtitle, cta, meta } = CONTENT.hero;
  const reduceMotion = useReducedMotion();
  const isDesktop = useIsDesktop();
  const sectionRef = useRef(null);
  // Scroll-linked parallax is desktop-only: on a phone it costs frames during
  // the most performance-sensitive scroll on the site and the travel is
  // invisible on a short viewport.
  const useParallax = isDesktop && !reduceMotion;

  // Gentle scroll parallax on the portrait — transform only, so it stays on
  // the compositor and never affects layout.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);

  const rise = (delay, distance = 22) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: distance },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, delay, ease: EASE },
        };

  return (
    <section
      className={styles.hero}
      id="home"
      ref={sectionRef}
      aria-label="Introduzione al Metodo ALPS"
    >
      <div className={styles.inner}>
        <div className={styles.textContainer}>
          {eyebrow && (
            <motion.span className={styles.eyebrow} {...rise(0, 14)}>
              {eyebrow}
            </motion.span>
          )}

          {/*
            Four spans, four deliberate lines. The trailing space inside each
            span keeps the accessible name and any text extraction identical
            to the original sentence.
          */}
          <h1 className={styles.headline} itemProp="headline">
            <motion.span className={styles.hLead} {...rise(0.06, 16)}>
              {title.lead}{" "}
            </motion.span>
            {title.main.map((line, index) => (
              <motion.span
                key={line}
                className={styles.hMain}
                {...rise(0.12 + index * 0.08)}
              >
                {line}{" "}
              </motion.span>
            ))}
            <motion.span className={styles.hAccent} {...rise(0.3)}>
              {title.accent}
            </motion.span>
          </h1>

          <motion.p
            className={styles.subheadline}
            itemProp="description"
            {...rise(0.42, 18)}
          >
            {setText(subtitle.lead)}
            <em className={styles.subEmphasis}>{subtitle.emphasis}</em>
            {subtitle.tail}
          </motion.p>

          <motion.div className={styles.actions} {...rise(0.52, 18)}>
            <CtaButton
              to="register"
              className={styles.cta}
              aria-label="Candidati al percorso ALPS"
            >
              {setText(cta)}
            </CtaButton>
          </motion.div>

          {meta && (
            <motion.dl className={styles.meta} {...rise(0.62, 16)}>
              {meta.map((item) => (
                <div key={item.value} className={styles.metaItem}>
                  <dt>{item.value}</dt>
                  <dd>{item.label}</dd>
                </div>
              ))}
            </motion.dl>
          )}
        </div>

        <div className={styles.imageColumn}>
          <motion.div
            className={styles.imageContainer}
            style={useParallax ? { y: imageY } : undefined}
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, scale: 1.05 },
                  animate: { opacity: 1, scale: 1 },
                  transition: { duration: 1.2, delay: 0.1, ease: EASE },
                })}
          >
            <img
              src={heroImage}
              srcSet={HERO_SRCSET}
              sizes="(min-width: 993px) 46vw, 100vw"
              width="1620"
              height="1080"
              alt="Caterina Imola, insegnante di italiano per stranieri con metodo scientifico ALPS"
              itemProp="image"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </motion.div>

          <AlpsCaption reduceMotion={reduceMotion} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
